import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import Footer from "@/sections/Footer";

// Slim reusable layout for legal / policy pages. Left-aligned prose with a
// sticky in-page table of contents on desktop. Kept intentionally light —
// these pages are read, not admired.
export default function PolicyLayout({
  eyebrow,
  title,
  updated,
  intro,
  toc = [],
  children,
  testId,
}) {
  return (
    <div data-testid={testId} className="bg-white min-h-screen text-slate-900">
      <MarketplaceNav />
      <section className="max-w-[1180px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10 md:pb-14">
        <div className="max-w-[68ch]">
          <div className="text-[11px] tracking-[0.24em] uppercase text-[#2563EB] font-semibold">
            § {eyebrow}
          </div>
          <h1 className="mt-4 font-display font-medium text-[44px] md:text-[68px] leading-[0.98] tracking-[-0.04em] text-slate-900">
            {title}
          </h1>
          {updated && (
            <p className="mt-4 text-[12.5px] text-slate-500">
              Last updated <span className="text-slate-900 font-medium">{updated}</span>
            </p>
          )}
          {intro && (
            <p className="mt-6 text-slate-700 text-lg leading-relaxed max-w-[62ch]">
              {intro}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-[1180px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-[220px_minmax(0,1fr)] gap-10 md:gap-16">
          {/* Table of contents */}
          <aside className="hidden md:block">
            <div className="sticky top-24">
              <div className="eyebrow mb-3">§ On this page</div>
              <ol className="space-y-2 text-[13px] text-slate-600">
                {toc.map((item, i) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="group inline-flex items-start gap-1.5 hover:text-slate-900 transition-colors"
                    >
                      <ChevronRight className="w-3 h-3 mt-0.5 text-slate-300 group-hover:text-[#2563EB]" />
                      <span>
                        <span className="text-slate-400 mr-1">{String(i + 1).padStart(2, "0")}</span>
                        {item.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-10 rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">§ Questions?</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">
                  Email{" "}
                  <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">
                    support@coachly.app
                  </a>{" "}
                  and we'll get back within one business day.
                </p>
              </div>
            </div>
          </aside>

          {/* Body */}
          <article className="prose prose-slate max-w-none text-[15.5px] text-slate-700 leading-[1.75]">
            {children}
          </article>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200 text-[12.5px] text-slate-500 max-w-[62ch]">
          <p>
            This document is a good-faith MVP policy for Coachly, an
            independent-coach marketplace based in the United States. It is
            not a substitute for legal advice. Coaches and clients with
            specific concerns should contact their own attorney. If you spot
            something that needs to change, write to{" "}
            <a href="mailto:legal@coachly.app" className="text-[#2563EB] hover:underline">
              legal@coachly.app
            </a>
            .
          </p>
          <p className="mt-4">
            <Link to="/" className="text-[#2563EB] hover:underline">← Back to Coachly</Link>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section building blocks — keep templates readable & consistent.
// ---------------------------------------------------------------------------
export function PolicySection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-12">
      <h2
        className="font-display font-medium text-[26px] tracking-tight text-slate-900 mt-6 mb-4"
        id={`h-${id}`}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export function P({ children }) {
  return <p className="mt-3 text-slate-700 leading-[1.75]">{children}</p>;
}

export function UL({ children }) {
  return (
    <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-slate-400">
      {children}
    </ul>
  );
}

export function OL({ children }) {
  return (
    <ol className="mt-4 space-y-2 pl-5 list-decimal marker:text-slate-400">
      {children}
    </ol>
  );
}

export function LI({ children }) {
  return <li className="text-slate-700 leading-[1.7]">{children}</li>;
}

export function Callout({ children, tone = "blue" }) {
  const bg = tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-[#2563EB]/5 border-[#2563EB]/20 text-slate-800";
  return (
    <div className={`mt-5 rounded-xl border p-4 text-[14px] leading-relaxed ${bg}`}>
      {children}
    </div>
  );
}
